import { useMemo } from 'react'

interface Card {
  card_id: number
  title: string
  icon_url: string
  categories?: any[]
}

interface CardsData {
  cards: Card[]
}

interface UseFavoritesDataProps {
  cardsData: CardsData | undefined
  favorites: string[]
  searchQuery: string
  categoriesData: any
}

export const useFavoritesData = ({
  cardsData,
  favorites,
  searchQuery,
  categoriesData,
}: UseFavoritesDataProps) => {
  // Получаем карточки из избранного
  const favoriteCards = useMemo(() => {
    if (!cardsData?.cards || !favorites || favorites.length === 0) {
      return []
    }

    let cards = cardsData.cards.filter((card: Card) =>
      favorites.includes(card.card_id.toString())
    )

    // Фильтруем по поисковому запросу
    if (searchQuery.trim()) {
      cards = cards.filter((card: Card) =>
        card.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    }

    return cards
  }, [cardsData, favorites, searchQuery])

  // Функция для поиска категории по ID
  const findCategoryById = (categories: any[], categoryId: number): any => {
    for (const category of categories) {
      if (category.category_id === categoryId) {
        return category
      }
      if (category.subcategories) {
        const found = findCategoryById(category.subcategories, categoryId)
        if (found) return found
      }
    }
    return null
  }

  // Функция для получения полного пути категории
  const getCategoryPath = (
    categories: any[],
    categoryId: number
  ): { main: any; sub: any; subSub?: any } | null => {
    for (const mainCategory of categories) {
      if (mainCategory.subcategories) {
        for (const subCategory of mainCategory.subcategories) {
          if (
            subCategory.card_ids &&
            subCategory.card_ids.includes(categoryId)
          ) {
            return { main: mainCategory, sub: subCategory }
          }
          // Проверяем вложенные подкатегории
          if (subCategory.subcategories) {
            for (const subSubCategory of subCategory.subcategories) {
              if (
                subSubCategory.card_ids &&
                subSubCategory.card_ids.includes(categoryId)
              ) {
                return {
                  main: mainCategory,
                  sub: subCategory,
                  subSub: subSubCategory,
                }
              }
            }
          }
        }
      }
    }
    return null
  }

  // Группируем избранные карточки по иерархии категорий
  const hierarchicalFavoriteCards = useMemo(() => {
    if (!categoriesData?.categories || favoriteCards.length === 0) {
      return {}
    }

    const hierarchy: {
      [mainKey: string]: {
        main: any
        subcategories: {
          [subKey: string]: {
            sub: any
            subSubcategories: {
              [subSubKey: string]: {
                subSub: any
                cards: any[]
              }
            }
          }
        }
      }
    } = {}

    favoriteCards.forEach((card: Card) => {
      const categoryPath = getCategoryPath(
        categoriesData.categories,
        card.card_id
      )
      if (categoryPath) {
        const mainKey = categoryPath.main.category_id.toString()
        const subKey = categoryPath.sub.category_id.toString()
        const subSubKey = categoryPath.subSub
          ? categoryPath.subSub.category_id.toString()
          : 'direct'

        // Создаем структуру если её нет
        if (!hierarchy[mainKey]) {
          hierarchy[mainKey] = {
            main: categoryPath.main,
            subcategories: {},
          }
        }

        if (!hierarchy[mainKey].subcategories[subKey]) {
          hierarchy[mainKey].subcategories[subKey] = {
            sub: categoryPath.sub,
            subSubcategories: {},
          }
        }

        if (
          !hierarchy[mainKey].subcategories[subKey].subSubcategories[subSubKey]
        ) {
          hierarchy[mainKey].subcategories[subKey].subSubcategories[subSubKey] =
            {
              subSub: categoryPath.subSub || null,
              cards: [],
            }
        }

        hierarchy[mainKey].subcategories[subKey].subSubcategories[
          subSubKey
        ].cards.push(card)
      }
    })

    return hierarchy
  }, [favoriteCards, categoriesData])

  return {
    favoriteCards,
    hierarchicalFavoriteCards,
  }
} 